// `chrome-extension.extension-tabs.client-contacts.client-contact.profile.more` (and its twin
// under the review screen) — the contact's "More" view.
//
// ⚠️ THE TWO PRODUCTS' "MORE" VIEWS SHOW DIFFERENT THINGS, which is why this is not
// `CandidateResumeRoute` with a flag. The candidate's More is an iframe over their CV; the
// contact's is the companies behind them — a contact has no CV, and the endpoint is a different
// one answering a different shape.
//
// ⚠️ AND IT OPENS ONLY WITH AN `algoScore`, like every other More view: `isInViewMoreState()` is
// a params comparison, so the transclusion this fills is not rendered without one. The parent
// decides that; this route is what goes inside it.
import { useEffect, useState, type ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getClientContactPreviewAndSummary, type ClientContactPreviewAndSummary } from '@woo-ext/api'
import { ExtensionViewWidth } from '@woo-ext/protocol'
import { ClientContactPreview } from '@woo-ext/ui'

export function ClientContactPreviewRoute(): ReactElement {
  const [params] = useSearchParams()
  const [preview, setPreview] = useState<ClientContactPreviewAndSummary | undefined>(undefined)
  const [loaded, setLoaded] = useState(false)

  const rawContact = params.get('clientContactId')
  const parsedContact = rawContact === null ? Number.NaN : Number.parseInt(rawContact, 10)
  const contactId = Number.isNaN(parsedContact) ? undefined : parsedContact
  const rawAlert = params.get('alertId')
  const parsedAlert = rawAlert === null ? Number.NaN : Number.parseInt(rawAlert, 10)
  const alertId = Number.isNaN(parsedAlert) ? undefined : parsedAlert

  useEffect(() => {
    if (contactId === undefined) return undefined
    let live = true
    getClientContactPreviewAndSummary({
      clientContactId: contactId,
      // ⚠️ SENT WHEN THE URL HAS ONE, ABSENT OTHERWISE — upstream reads both straight off
      // `$stateParams`, so the standalone profile asks without an alert and the review twin
      // asks with one. Defaulting it here would ask the server a question this screen is not
      // being shown the answer to.
      ...(alertId === undefined ? {} : { alertId }),
    }).then(
      (answer) => {
        if (!live) return
        setPreview(answer)
        setLoaded(true)
      },
      () => {
        // `$q.all(...).then(…)` with no rejection handler upstream: a failed call leaves
        // `loaded` false and the spinner turning, which is the same shape the candidate
        // profile's preview has and is recorded there too.
        return undefined
      },
    )
    return () => {
      live = false
    }
  }, [contactId, alertId])

  return (
    <ClientContactPreview
      // `maxWidth = ExtensionViewWidth.Detailed` on the More state's controller, bound to the
      // element's `flex-basis` — see the prop.
      flexBasis={ExtensionViewWidth.Detailed}
      loaded={loaded}
      currentCompanies={preview?.currentCompanies ?? []}
      additionalCompanies={preview?.additionalCompanies ?? []}
    />
  )
}
